import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast, useToastStore } from '../store/toastStore';
import { announce } from '../components/ui/LiveAnnouncer';
import { UNDO_WINDOW_MS, type PendingCommit } from '../utils/undoableCommit';
import { createUnloadAwareCommit } from '../utils/undoableUnload';
import type { UnloadRequest } from '../utils/undoableActionsCore';
import { sendKeepalive } from '../utils/undoableActions';

/**
 * An action with "ביטול" (undo) through a **delayed commit** (PRD §4.8, B.23):
 * the request is sent only after the 6 s window, so undo never sends a reverse
 * request (closing a ticket never becomes a reopen).
 *
 *   const close = useUndoableMutation({
 *     mutationFn: (id: string) => api.updateTicketStatus(id, 'closed'),
 *     message: t('…closed'),               // "הקריאה נסגרה"
 *     onSchedule: (id) => hideRowOptimistically(id),
 *     onUndo: (id) => showRowAgain(id),
 *     onSuccess: () => queryClient.invalidateQueries({ queryKey: ['tickets'] }),
 *     errorMessage: t('…closeFailed'),
 *   });
 *   close.mutate(ticket.id);
 *
 * Pending commits are sent at once when the component unmounts or the page is
 * hidden (navigation, tab close), so leaving never drops an action.
 * DQ-115: give `unload` (the same request as plain HTTP) and, when the page is
 * left or hidden, the action goes out as `fetch` with `keepalive` - an XHR sent
 * then is cancelled by the browser.
 */
export interface UndoableMutationOptions<V, R> {
  mutationFn: (vars: V) => Promise<R>;
  /** Toast text shown during the window, past passive ("הקריאה נסגרה"). */
  message: string | ((vars: V) => string);
  /** Error toast when the delayed request fails (closes by itself, D-153). */
  errorMessage?: string | ((error: unknown, vars: V) => string);
  windowMs?: number;
  /** Runs at once on `mutate` (optimistic UI). */
  onSchedule?: (vars: V) => void;
  /** Runs when the user undoes (revert the optimistic UI). */
  onUndo?: (vars: V) => void;
  onSuccess?: (result: R, vars: V) => void;
  onError?: (error: unknown, vars: V) => void;
  /** DQ-115: the same request for a closing tab (`fetch` + `keepalive`), e.g. `{ method: 'POST', path: '<the action endpoint>' }`. Only flushes an action the user already clicked; never started by the tab closing. */
  unload?: (vars: V) => UnloadRequest;
}

export interface UndoableMutation<V> {
  mutate: (vars: V) => PendingCommit;
  /** True while at least one action waits in its undo window or is being sent. */
  isPending: boolean;
  /** Send every waiting action now. */
  flushAll: () => Promise<void>;
}

export function useUndoableMutation<V, R = unknown>(options: UndoableMutationOptions<V, R>): UndoableMutation<V> {
  const { t } = useTranslation();
  const optionsRef = useRef(options);
  optionsRef.current = options;
  const [inFlight, setInFlight] = useState(0);
  const committerRef = useRef<ReturnType<typeof createUnloadAwareCommit<V, R>> | null>(null);
  // Undo toast per pending action, removed as soon as the action can no longer be undone.
  const toastIds = useRef(new Map<number, string>());

  if (!committerRef.current) {
    const settle = (id: number) => {
      setInFlight((n) => Math.max(0, n - 1));
      const toastId = toastIds.current.get(id);
      toastIds.current.delete(id);
      if (toastId) useToastStore.getState().removeToast(toastId);
    };
    committerRef.current = createUnloadAwareCommit<V, R>({
      windowMs: options.windowMs ?? UNDO_WINDOW_MS,
      commit: (vars) => optionsRef.current.mutationFn(vars),
      unload: options.unload ? (vars) => optionsRef.current.unload!(vars) : undefined,
      onCommitted: (result, vars, id) => { settle(id); optionsRef.current.onSuccess?.(result, vars); },
      onUndone: (vars, id) => { settle(id); optionsRef.current.onUndo?.(vars); },
      onError: (error, vars, id) => {
        settle(id);
        const o = optionsRef.current;
        o.onUndo?.(vars); // the action did not happen: put the optimistic UI back
        const msg = typeof o.errorMessage === 'function' ? o.errorMessage(error, vars) : o.errorMessage;
        if (msg) toast('error', msg);
        o.onError?.(error, vars);
      },
    });
  }

  // Never drop a waiting action: send it when the page is hidden or the owner unmounts.
  // DQ-115: on pagehide (and, for an action with `unload`, a hidden tab) by keepalive.
  useEffect(() => {
    const committer = committerRef.current!;
    const onHide = () => { void committer.flushOnHide(sendKeepalive); };
    const onVisibility = () => { if (document.visibilityState === 'hidden' && committer.hasUnload) onHide(); };
    window.addEventListener('pagehide', onHide);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('pagehide', onHide);
      document.removeEventListener('visibilitychange', onVisibility);
      void committer.flushAll();
    };
  }, []);

  const mutate = useCallback((vars: V) => {
    const o = optionsRef.current;
    const committer = committerRef.current!;
    setInFlight((n) => n + 1);
    o.onSchedule?.(vars);
    const pending = committer.schedule(vars);
    const message = typeof o.message === 'function' ? o.message(vars) : o.message;
    const toastId = toast('success', message, {
      // The toast may outlive the window while hovered; it is removed on commit.
      duration: o.windowMs ?? UNDO_WINDOW_MS,
      action: {
        label: t('common.undo'),
        onClick: () => {
          if (pending.undo()) announce(t('shell.undone'));
        },
      },
    });
    toastIds.current.set(pending.id, toastId);
    return pending;
  }, [t]);

  const flushAll = useCallback(() => committerRef.current!.flushAll(), []);

  return { mutate, isPending: inFlight > 0, flushAll };
}
